import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Logo } from "@/components/Logo";

function useMeta(title, description) {
  useEffect(() => {
    document.title = title;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    const prev = m.getAttribute("content");
    m.setAttribute("content", description);
    return () => { if (prev) m.setAttribute("content", prev); };
  }, [title, description]);
}

export function LegalLayout({ title, subtitle, updated, toc, children, metaTitle, metaDescription }) {
  const navigate = useNavigate();
  const location = useLocation();
  useMeta(metaTitle, metaDescription);

  const navItems = [
    { label: "Home", to: "/" },
    { label: "Privacy", to: "/privacy" },
    { label: "Terms", to: "/terms" },
  ];

  return (
    <div className="min-h-screen bg-decide-canvas">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-decide-canvas/85 border-b border-decide-line/70">
        <div className="mx-auto max-w-5xl flex items-center justify-between px-6 h-16">
          <button onClick={() => navigate("/")} aria-label="DECIDE home"><Logo size="md" /></button>
          <nav aria-label="Primary" className="flex items-center gap-1 sm:gap-2">
            {navItems.map((n) => {
              const active = location.pathname === n.to;
              return (
                <button key={n.to} onClick={() => navigate(n.to)} className={`rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${active ? "text-decide-forest" : "text-decide-slate hover:text-decide-ink"}`}>
                  {n.label}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6">
        <section className="pt-14 pb-10 border-b border-decide-line">
          <p className="decide-label">DECIDE</p>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-decide-ink leading-[1.03]">{title}</h1>
          {subtitle && <p className="mt-4 max-w-xl text-lg leading-relaxed text-decide-slate">{subtitle}</p>}
          {updated && <p className="mt-5 text-sm text-decide-mute">Last updated: {updated}</p>}
        </section>

        <div className="grid lg:grid-cols-[240px_1fr] gap-10 lg:gap-14 py-12">
          {toc && (
            <aside className="lg:sticky lg:top-24 lg:self-start" aria-label="Table of contents">
              <p className="decide-label mb-3">Contents</p>
              <ol className="space-y-2">
                {toc.map((s, i) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className="group flex gap-2 text-[13.5px] leading-snug text-decide-slate transition-colors hover:text-decide-forest">
                      <span className="font-mono text-[11px] text-decide-mute pt-0.5">{String(i + 1).padStart(2, "0")}</span>
                      <span>{s.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </aside>
          )}

          <article className="min-w-0 max-w-[68ch]">{children}</article>
        </div>
      </main>

      <footer className="border-t border-decide-line">
        <div className="mx-auto max-w-5xl px-6 py-10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <Logo size="sm" />
            <nav aria-label="Footer" className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <button onClick={() => navigate("/privacy")} className="text-decide-slate hover:text-decide-ink transition-colors">Privacy Policy</button>
              <button onClick={() => navigate("/terms")} className="text-decide-slate hover:text-decide-ink transition-colors">Terms of Service</button>
              <a href="#contact" className="text-decide-slate hover:text-decide-ink transition-colors">Contact</a>
            </nav>
          </div>
          <p className="mt-6 text-[13px] text-decide-mute">
            © {new Date().getFullYear()} DECIDE. Operated by DGG Labs. DECIDE structures your thinking — it is not professional advice.
          </p>
        </div>
      </footer>
    </div>
  );
}

// Reusable content primitives for a clean editorial legal page
export function LegalSection({ id, index, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mb-11">
      <h2 className="flex items-baseline gap-3 text-2xl sm:text-[28px] font-medium tracking-tight text-decide-ink">
        {index != null && <span className="font-mono text-sm text-decide-forest">{String(index).padStart(2, "0")}</span>}
        <span>{title}</span>
      </h2>
      <div className="mt-4 space-y-4 text-[15.5px] leading-[1.75] text-decide-slate">{children}</div>
    </section>
  );
}

export function Placeholder({ children }) {
  return (
    <span className="rounded-md bg-decide-warnSoft px-1.5 py-0.5 font-mono text-[13px] font-semibold text-decide-warn">{children}</span>
  );
}
